import React, { useState, useEffect } from "react";
import {
  FiCheck,
  FiArrowRight,
  FiEdit2,
  FiPlus,
  FiX,
  FiExternalLink,
} from "react-icons/fi";
import {
  postRequestAuthenticated,
  postRequestAuthenticatedWithFile,
} from "../../../services/rest";
import { toast } from "../../../utils/toast";

const ORDER = [
  "signup",
  "crawl_done",
  "domain_pending",
  "domain_verified",
  "draft_ready",
  "community_committed",
  "embed_live",
  "complete",
];

const STEPS = [
  { label: "Account created", at: "signup" },
  { label: "Domain verified", at: "domain_verified" },
  { label: "Community created", at: "community_committed" },
  { label: "Embed installed & live", at: "embed_live" },
];

const statusOf = (state) => {
  if (state === "embed_live" || state === "complete")
    return { label: "Live", tone: "success" };
  if (["domain_verified", "draft_ready", "community_committed"].includes(state))
    return { label: "Domain verified", tone: "success" };
  if (state === "domain_pending")
    return { label: "Awaiting DNS", tone: "warning" };
  return { label: "Setup in progress", tone: "warning" };
};

const emptyProfile = {
  displayName: "",
  logo: "",
  banner: "",
  tagline: "",
  description: "",
  links: [],
  sections: [],
};

const Input = (props) => (
  <input
    {...props}
    className="w-full rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-2.5 text-sm text-theme-secondaryText outline-none placeholder:text-theme-placeholder"
  />
);

const Stat = ({ label, value }) => (
  <div>
    <p className="text-[11px] font-semibold uppercase tracking-wide text-theme-placeholder">
      {label}
    </p>
    <p className="mt-1 truncate text-sm text-theme-secondaryText">
      {value || "—"}
    </p>
  </div>
);

const OverviewPanel = ({ business, onContinue }) => {
  const state = business?.onboardingState || "signup";
  const currentIdx = Math.max(0, ORDER.indexOf(state));
  const steps = STEPS.map((s) => ({
    ...s,
    done: currentIdx >= ORDER.indexOf(s.at),
  }));
  const doneCount = steps.filter((s) => s.done).length;
  const firstPending = steps.find((s) => !s.done);
  const pct = Math.round((doneCount / steps.length) * 100);
  const allDone = doneCount === steps.length;
  const status = statusOf(state);

  const [profile, setProfile] = useState(emptyProfile);
  const [draft, setDraft] = useState(emptyProfile);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    postRequestAuthenticated("/embed/config", {})
      .then((res) => {
        if (active && res?.success && res.config?.branding) {
          const b = res.config.branding;
          setProfile({
            displayName: b.displayName || "",
            logo: b.logo || "",
            banner: b.banner || "",
            tagline: b.tagline || "",
            description: b.description || "",
            links: Array.isArray(b.links) ? b.links : [],
            sections: Array.isArray(b.sections) ? b.sections : [],
          });
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const startEdit = () => {
    setDraft(profile);
    setEditing(true);
  };

  // Upload a logo/banner image -> CloudFront URL -> fill the field.
  const [uploading, setUploading] = useState("");
  const uploadImage = async (file, field) => {
    if (!file) return;
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await postRequestAuthenticatedWithFile("/upload/image", fd);
      if (res?.success && res.url) {
        setDraft((d) => ({ ...d, [field]: res.url }));
      } else {
        toast("Upload failed. Try again.", "error");
      }
    } catch (e) {
      toast("Upload failed. Try again.", "error");
    } finally {
      setUploading("");
    }
  };

  const setLink = (i, key, val) =>
    setDraft((d) => ({
      ...d,
      links: d.links.map((l, idx) => (idx === i ? { ...l, [key]: val } : l)),
    }));
  const addLink = () =>
    setDraft((d) => ({ ...d, links: [...d.links, { label: "", url: "" }] }));
  const removeLink = (i) =>
    setDraft((d) => ({ ...d, links: d.links.filter((_, idx) => idx !== i) }));

  const setSection = (i, key, val) =>
    setDraft((d) => ({
      ...d,
      sections: d.sections.map((s, idx) =>
        idx === i ? { ...s, [key]: val } : s
      ),
    }));
  const addSection = () =>
    setDraft((d) => ({
      ...d,
      sections: [...(d.sections || []), { title: "", body: "" }],
    }));
  const removeSection = (i) =>
    setDraft((d) => ({
      ...d,
      sections: d.sections.filter((_, idx) => idx !== i),
    }));

  const save = () => {
    setSaving(true);
    const cleaned = {
      ...draft,
      links: draft.links.filter((l) => l.label?.trim() && l.url?.trim()),
      sections: (draft.sections || []).filter(
        (s) => s.title?.trim() || s.body?.trim()
      ),
    };
    postRequestAuthenticated("/embed/config/update", { branding: cleaned })
      .then((res) => {
        if (res?.success) {
          setProfile(cleaned);
          setEditing(false);
        }
      })
      .finally(() => setSaving(false));
  };

  const name = profile.displayName || business?.name || "Your business";
  const initial = name.charAt(0).toUpperCase();
  const plan = business?.current_subscription?.planId;
  const apiKey = business?.apiKey;

  return (
    <div className="mx-auto max-w-5xl px-4 py-7 sm:px-6 lg:px-8">
      <div className="mb-7 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-theme-secondaryText">
            Welcome{business?.name ? `, ${business.name}` : ""}
          </h1>
          <p className="mt-1 text-sm text-theme-placeholder">
            Your community at a glance.
          </p>
        </div>
        {allDone && !editing && (
          <button
            onClick={startEdit}
            className="flex flex-shrink-0 items-center gap-2 rounded-lg border border-theme-chatDivider px-3 py-2 text-sm font-medium text-theme-primaryText transition-colors hover:bg-theme-secondaryBackground"
          >
            <FiEdit2 size={14} /> Edit profile
          </button>
        )}
      </div>

      {/* Setup progress hero (until fully live) */}
      {!allDone && (
        <div className="mb-6 rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-6">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-theme-secondaryText">
                Finish setting up your community
              </h2>
              <p className="mt-1 text-sm text-theme-placeholder">
                {doneCount} of {steps.length} steps done · let's get your embed
                live.
              </p>
            </div>
            <button
              onClick={onContinue}
              className="flex flex-shrink-0 items-center gap-2 rounded-lg bg-theme-accent px-4 py-2 text-sm font-semibold text-theme-accentText transition-colors hover:bg-theme-accentHover"
            >
              Continue setup <FiArrowRight size={16} />
            </button>
          </div>
          <div className="mb-5 h-2 w-full overflow-hidden rounded-full bg-theme-tertiaryBackground">
            <div
              className="h-full rounded-full bg-theme-accent transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex flex-col gap-2.5">
            {steps.map((s) => {
              const isActive = firstPending && s.label === firstPending.label;
              return (
                <div key={s.label} className="flex items-center gap-3">
                  <span
                    className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border ${
                      s.done
                        ? "border-theme-accent bg-theme-accent text-theme-accentText"
                        : isActive
                        ? "border-theme-accent text-theme-accent"
                        : "border-theme-chatDivider text-transparent"
                    }`}
                  >
                    {s.done ? (
                      <FiCheck size={12} />
                    ) : (
                      <span className="h-1.5 w-1.5 rounded-full bg-current" />
                    )}
                  </span>
                  <span
                    className={`text-sm ${
                      s.done
                        ? "text-theme-placeholder line-through"
                        : isActive
                        ? "font-semibold text-theme-secondaryText"
                        : "text-theme-primaryText"
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {editing ? (
        /* ---- editor ---- */
        <div className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-6">
          <h2 className="mb-4 text-base font-semibold text-theme-secondaryText">
            Edit brand profile
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
                Display name
              </label>
              <Input
                value={draft.displayName}
                onChange={(e) => setDraft((d) => ({ ...d, displayName: e.target.value }))}
                placeholder="Acme Community"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
                Tagline
              </label>
              <Input
                value={draft.tagline}
                onChange={(e) => setDraft((d) => ({ ...d, tagline: e.target.value }))}
                placeholder="Where Acme builders hang out"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
                Logo URL
              </label>
              <div className="flex items-center gap-2">
                <Input
                  value={draft.logo}
                  onChange={(e) => setDraft((d) => ({ ...d, logo: e.target.value }))}
                  placeholder="https://…/logo.png"
                />
                <label className="flex-shrink-0 cursor-pointer rounded-lg border border-theme-chatDivider px-3 py-2.5 text-xs font-semibold text-theme-primaryText transition-colors hover:bg-theme-tertiaryBackground">
                  {uploading === "logo" ? "…" : "Upload"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => uploadImage(e.target.files?.[0], "logo")}
                  />
                </label>
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
                Banner image URL
              </label>
              <div className="flex items-center gap-2">
                <Input
                  value={draft.banner}
                  onChange={(e) => setDraft((d) => ({ ...d, banner: e.target.value }))}
                  placeholder="https://…/banner.jpg"
                />
                <label className="flex-shrink-0 cursor-pointer rounded-lg border border-theme-chatDivider px-3 py-2.5 text-xs font-semibold text-theme-primaryText transition-colors hover:bg-theme-tertiaryBackground">
                  {uploading === "banner" ? "…" : "Upload"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => uploadImage(e.target.files?.[0], "banner")}
                  />
                </label>
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
                Description
              </label>
              <textarea
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                rows={3}
                placeholder="What your community is about…"
                className="w-full resize-none rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-2.5 text-sm text-theme-secondaryText outline-none placeholder:text-theme-placeholder"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
              Links
            </label>
            <div className="flex flex-col gap-2">
              {draft.links.map((l, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={l.label}
                    onChange={(e) => setLink(i, "label", e.target.value)}
                    placeholder="Website"
                  />
                  <Input
                    value={l.url}
                    onChange={(e) => setLink(i, "url", e.target.value)}
                    placeholder="https://…"
                  />
                  <button
                    type="button"
                    onClick={() => removeLink(i)}
                    className="flex-shrink-0 rounded-md p-2 text-theme-placeholder hover:bg-theme-tertiaryBackground hover:text-theme-error"
                  >
                    <FiX size={16} />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addLink}
              className="mt-2 flex items-center gap-1.5 text-sm font-medium text-theme-accent"
            >
              <FiPlus size={15} /> Add link
            </button>
          </div>

          {/* Custom sections */}
          <div className="mt-5 border-t border-theme-chatDivider pt-5">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
              Custom sections
            </label>
            <p className="mb-2 text-xs text-theme-placeholder">
              Add your own content boxes — about, FAQs, announcements, anything.
            </p>
            <div className="flex flex-col gap-3">
              {(draft.sections || []).map((s, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-theme-chatDivider p-3"
                >
                  <div className="flex items-center gap-2">
                    <Input
                      value={s.title}
                      onChange={(e) => setSection(i, "title", e.target.value)}
                      placeholder="Section title"
                    />
                    <button
                      type="button"
                      onClick={() => removeSection(i)}
                      className="flex-shrink-0 rounded-md p-2 text-theme-placeholder hover:bg-theme-tertiaryBackground hover:text-theme-error"
                    >
                      <FiX size={16} />
                    </button>
                  </div>
                  <textarea
                    value={s.body}
                    onChange={(e) => setSection(i, "body", e.target.value)}
                    rows={3}
                    placeholder="Section content…"
                    className="mt-2 w-full resize-none rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-2.5 text-sm text-theme-secondaryText outline-none placeholder:text-theme-placeholder"
                  />
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addSection}
              className="mt-2 flex items-center gap-1.5 text-sm font-medium text-theme-accent"
            >
              <FiPlus size={15} /> Add section
            </button>
          </div>

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              onClick={() => setEditing(false)}
              className="rounded-lg border border-theme-chatDivider px-4 py-2 text-sm font-medium text-theme-primaryText hover:bg-theme-tertiaryBackground"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="rounded-lg bg-theme-accent px-5 py-2.5 text-sm font-semibold text-theme-accentText hover:bg-theme-accentHover disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save profile"}
            </button>
          </div>
        </div>
      ) : (
        /* ---- profile mini-site ---- */
        <div className="overflow-hidden rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground">
          {profile.banner ? (
            <img
              src={profile.banner}
              alt="banner"
              className="h-52 w-full object-cover"
            />
          ) : (
            <div
              className="h-52 w-full"
              style={{
                background:
                  "linear-gradient(120deg, rgba(242,97,27,.45), rgba(124,58,237,.4))",
              }}
            />
          )}
          <div className="px-6 pb-6">
            <div className="-mt-8 flex flex-wrap items-end gap-4">
              {profile.logo ? (
                <img
                  src={profile.logo}
                  alt={name}
                  className="h-16 w-16 flex-shrink-0 rounded-2xl object-cover ring-4 ring-theme-secondaryBackground"
                />
              ) : (
                <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-theme-accent text-2xl font-bold text-theme-accentText ring-4 ring-theme-secondaryBackground">
                  {initial}
                </div>
              )}
              <div className="min-w-0 flex-1 pb-1">
                <h2 className="truncate text-xl font-semibold text-theme-secondaryText">
                  {name}
                </h2>
                {profile.tagline && (
                  <p className="truncate text-sm text-theme-placeholder">
                    {profile.tagline}
                  </p>
                )}
              </div>
              <span
                className={`mb-1 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${
                  status.tone === "success"
                    ? "border-theme-success text-theme-success"
                    : "border-theme-warning text-theme-warning"
                }`}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                {status.label}
              </span>
            </div>

            {profile.description && (
              <p className="mt-4 text-sm leading-relaxed text-theme-primaryText">
                {profile.description}
              </p>
            )}

            {profile.links?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {profile.links.map((l, i) => (
                  <a
                    key={i}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-full border border-theme-chatDivider px-3 py-1.5 text-xs font-medium text-theme-secondaryText hover:bg-theme-tertiaryBackground"
                  >
                    {l.label}
                    <FiExternalLink size={12} />
                  </a>
                ))}
              </div>
            )}

            <div className="mt-6 grid grid-cols-2 gap-5 border-t border-theme-chatDivider pt-5 sm:grid-cols-4">
              <Stat label="Plan" value={plan ? plan[0].toUpperCase() + plan.slice(1) : "No plan"} />
              <Stat label="Domain" value={business?.domain} />
              <Stat label="Owner" value={business?.user_id?.email} />
              <Stat
                label="Embed key"
                value={apiKey ? `${apiKey.slice(0, 10)}…${apiKey.slice(-4)}` : "Verify domain"}
              />
            </div>

            {profile.sections?.length > 0 && (
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {profile.sections.map((s, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-theme-chatDivider bg-theme-tertiaryBackground p-4"
                  >
                    {s.title && (
                      <h3 className="mb-1.5 text-sm font-semibold text-theme-secondaryText">
                        {s.title}
                      </h3>
                    )}
                    {s.body && (
                      <p
                        className="text-sm leading-relaxed text-theme-primaryText"
                        style={{ whiteSpace: "pre-line" }}
                      >
                        {s.body}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OverviewPanel;
